import { Suspense, useEffect, useRef, useState } from 'react'
import { recoverableLazy } from '@/components/ModuleRecovery/ModuleRecovery'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import {
  DEFAULT_MASCOT,
  SHEEP_DEFAULTS,
  parseMascotConfig,
  type MascotConfig,
} from '../mascot-catalog'
import { saveDonorMascot } from '../mascot-api'
import {
  campaignMascotBackdrop,
  isSheepAvailable,
  withMascotBackdrop,
  withRequiredMascotSpecies,
} from '../mascot-campaign'
import { DonorMascot } from '../DonorMascot'
import { useMascotGame } from './useMascotGame'
import { useGameSound } from './useGameSound'
import {
  miniGamesForHabitat,
  NEEDS,
  ROOMS,
  projectNeeds,
  timeLabel,
} from './mascot-game-view'
import { MascotCoin } from './MascotCoin'
import { MascotCoinReward } from './MascotCoinReward'
import { useCoinRewards } from './useCoinRewards'
import { MascotScene } from './MascotScene'
import { MascotRoomPanel } from './MascotRoomPanel'
import { MascotVeterinary } from './MascotVeterinary'
import { MascotEnergyPill } from './MascotEnergyPill'
import { MascotCarePurchaseButton, MascotCarePurchaseDialog } from './MascotCarePurchase'
import {
  hasWaitPass,
  mascotCareValue,
  type MascotCareBenefit,
} from './mascot-care-options'
import { MascotCommunity, MascotJournal, MascotShop } from './MascotCollection'
import { MascotMiniGame } from './MascotMiniGame'
import { GameConfirm } from './GameConfirm'
import { ActionCountdown } from './ActionCountdown'
import { MiniGameArtwork } from './MiniGameArtwork'
import { useLandingCampaignTopDonors } from '@/hooks/useLandingCampaignTopDonors'
import type {
  GameAction,
  GameEvent,
  GameSession,
  MiniGameKind,
  Room,
  ShopItem,
} from './mascot-game-types'
import styles from './MascotGame.module.css'
import type { AvatarConfig } from '../../donorAvatar/avatar-catalog'

const MascotEditor = recoverableLazy(() => import('../MascotEditor'))
const indicatorRooms = new Set<Room>([
  'journal',
  ...NEEDS.map((need) => need.room).filter((room) => room !== 'veterinary'),
])

export default function MascotGame({
  campaignId,
  mascot,
  avatar,
  onSaved,
  onClose,
  onVeterinaryPaid,
  requiredSpecies: campaignRequiredSpecies,
}: {
  campaignId: number
  mascot: MascotConfig | null
  avatar?: AvatarConfig | null
  onSaved: (config: MascotConfig) => void
  onClose: () => void
  onVeterinaryPaid?: () => void
  requiredSpecies?: 'jaguar'
}) {
  const coinRewards = useCoinRewards()
  const game = useMascotGame(campaignId, coinRewards.celebrate)
  const sound = useGameSound()
  const [room, setRoom] = useState<Room>('home')
  const [shop, setShop] = useState(false)
  const [shopCategory, setShopCategory] = useState<ShopItem['kind'] | 'coins'>('food')
  const [customize, setCustomize] = useState(false)
  const [carePurchase, setCarePurchase] = useState<MascotCareBenefit | null>(null)
  const [savedAppearance, setAppearance] = useState(mascot)
  const [session, setSession] = useState<GameSession | null>(null)
  const [autoStart, setAutoStart] = useState(false)
  const [reaction, setReaction] = useState('')
  const [petReaction, setPetReaction] = useState(0)
  const [careEffect, setCareEffect] = useState('')
  const [confirmClose, setConfirmClose] = useState(false)
  const [communityVisited, setCommunityVisited] = useState(false)
  const [appearanceRevision, setAppearanceRevision] = useState(0)
  const community = useLandingCampaignTopDonors(
    communityVisited ? campaignId : null,
    appearanceRevision
  )
  const dialog = useRef<HTMLDialogElement>(null)
  const wallet = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const reactionTimer = useRef<number | undefined>(undefined)
  const completedSession = useRef(false)
  const data = game.data
  const requiredSpecies =
    campaignRequiredSpecies ?? data?.catalog.required_species ?? undefined
  const contextualAppearance = savedAppearance
    ? withRequiredMascotSpecies(savedAppearance, requiredSpecies)
    : null
  const restrictedSheep =
    contextualAppearance?.species === 'sheep' && !isSheepAvailable(data?.catalog)
  const appearance = restrictedSheep ? null : contextualAppearance
  const state = data?.state
  const disabled = game.busy || game.loading || game.uncertain
  const requiredBackdrop = campaignMascotBackdrop(
    campaignId,
    data?.catalog.required_backdrop
  )
  const config = withMascotBackdrop(
    withRequiredMascotSpecies(
      appearance ?? { ...DEFAULT_MASCOT, name: 'Seu companheiro' },
      requiredSpecies
    )!,
    requiredBackdrop
  )
  const currentNeeds = state ? projectNeeds(state, game.now) : null
  const gameWait =
    state && hasWaitPass(state, 'game_start')
      ? 0
      : Math.max(0, (state?.cooldowns.game_start ?? 0) - game.now)
  const gameEnergy = data?.catalog.rules?.game_energy ?? 14
  const gameBlockedReason =
    gameWait > 0
      ? `Jogar em ${timeLabel(gameWait)}`
      : state?.sleeping
        ? 'Acorde o mascote para jogar'
        : (currentNeeds?.energy ?? 0) < gameEnergy
          ? `Descanse · precisa de ${gameEnergy} de energia`
          : (currentNeeds?.satiety ?? 0) < 15
            ? 'Alimente o mascote · mínimo 15 de saciedade'
            : game.uncertain
              ? 'Confirme a ação pendente'
              : game.busy
                ? 'Salvando…'
                : game.loading
                  ? 'Atualizando…'
                  : ''
  const jungle = config.species === 'jaguar' && config.backdrop !== 'farm'
  const gamePurchaseOptions = (
    <div className={styles.careOffers}>
      {(currentNeeds?.energy ?? 100) < gameEnergy && (
        <MascotCarePurchaseButton
          benefit="energy"
          disabled={disabled}
          onPurchase={setCarePurchase}
        />
      )}
      {(currentNeeds?.satiety ?? 100) < 15 && (
        <MascotCarePurchaseButton
          benefit="satiety"
          disabled={disabled}
          onPurchase={setCarePurchase}
        />
      )}
      {gameWait > 0 && (
        <MascotCarePurchaseButton
          benefit="wait_game_start"
          disabled={disabled}
          onPurchase={setCarePurchase}
        />
      )}
    </div>
  )
  const rooms = ROOMS.map((item) => {
    if (!jungle && config.backdrop === 'farm') {
      const farmRooms: Partial<
        Record<Room, { name: string; title: string; description: string }>
      > = {
        home: {
          name: 'Fazendinha',
          title: 'Seu cantinho no campo.',
          description: 'Entre o celeiro e o pomar, uma amizade para cuidar.',
        },
        kitchen: {
          name: 'Comedouro',
          title: 'Hora da comidinha.',
          description: 'Uma refeição caprichada depois de brincar na fazenda.',
        },
        bath: {
          name: 'Banho',
          title: 'Banho no quintal.',
          description: 'Água fresquinha para tirar a poeira da aventura.',
        },
        bedroom: {
          name: 'Casinha',
          title: 'Sossego no campo.',
          description: 'Um cantinho aconchegante para recarregar as energias.',
        },
        garden: {
          name: config.species === 'sheep' ? 'Pastagem' : 'Pomar',
          title:
            config.species === 'sheep'
              ? 'Cultive um pasto fofinho.'
              : 'Cultive seu pomar.',
          description:
            config.species === 'sheep'
              ? 'Plante e colha feno fresquinho para a sua ovelhinha.'
              : 'Plante, acompanhe e colha maçãs para seu companheiro.',
        },
      }
      return { ...item, ...farmRooms[item.id] }
    }
    if (!jungle) return item
    const overrides: Partial<
      Record<Room, { name: string; icon?: string; title: string; description: string }>
    > = {
      home: {
        name: 'Clareira',
        icon: 'mdi:forest',
        title: 'Seu refúgio na selva.',
        description: 'Entre folhas, rios e uma grande amizade.',
      },
      kitchen: {
        name: 'Alimentação',
        icon: 'mdi:food-steak',
        title: 'Carne para sua onça.',
        description: 'Escolha uma porção de carne para servir.',
      },
      bath: {
        name: 'Rio',
        title: 'Cuidados à beira do rio.',
        description: 'Água fresca e carinho para sua companheira.',
      },
      bedroom: {
        name: 'Abrigo',
        title: 'Descanso na mata.',
        description: 'Uma soneca tranquila no abrigo da floresta.',
      },
      garden: {
        name: 'Reserva',
        title: 'Cultive a floresta.',
        description: 'Restaure a mata e receba recompensas do cuidador.',
      },
    }
    return { ...item, ...overrides[item.id] }
  })
  const menuRooms = rooms.filter((item) => !indicatorRooms.has(item.id))
  const activeRoom = rooms.find((item) => item.id === room)!
  const showScene =
    !!data &&
    !!appearance?.name &&
    !shop &&
    room !== 'community' &&
    room !== 'veterinary' &&
    !!state &&
    !session

  useEffect(() => {
    const element = dialog.current
    const focus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const overflow = document.body.style.overflow
    element?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      clearTimeout(reactionTimer.current)
      element?.close()
      document.body.style.overflow = overflow
      focus?.focus({ preventScroll: true })
    }
  }, [])

  function goToRoom(next: Room) {
    if (next === 'community') setCommunityVisited(true)
    setRoom(next)
    setShop(false)
    setReaction('')
    panel.current?.scrollTo?.({ top: 0, behavior: 'instant' })
  }

  function openShop(category: ShopItem['kind'] | 'coins' = 'food') {
    setShopCategory(category)
    setShop(true)
    panel.current?.scrollTo?.({ top: 0, behavior: 'instant' })
  }

  async function act(action: GameAction) {
    if (action.action === 'pet') {
      setCareEffect('')
      setReaction(
        state?.sleeping
          ? 'Bons sonhos com seu carinho. ♡'
          : 'Você faz meu dia mais feliz! ♡'
      )
      setPetReaction((value) => value + 1)
      sound.play(2)
      clearTimeout(reactionTimer.current)
      reactionTimer.current = window.setTimeout(() => setReaction(''), 3500)
      // Affection is local and always available; only eligible rewards need a request.
      if (
        disabled ||
        state?.sleeping ||
        ((state?.cooldowns.pet ?? 0) > game.now && !hasWaitPass(state ?? {}, 'pet'))
      )
        return null
      return game.send(action)
    }
    const response = await game.send(action)
    if (response) {
      sound.play(2)
      if (['feed', 'wash', 'heal', 'snack', 'harvest'].includes(action.action)) {
        setCareEffect(
          action.action === 'wash'
            ? '🫧'
            : action.action === 'feed'
              ? (data?.catalog.items[action.item ?? '']?.icon ?? '🍎')
              : action.action === 'snack' || action.action === 'harvest'
                ? jungle
                  ? '🥩'
                  : config.species === 'sheep'
                    ? '🌾'
                    : '🍎'
                : action.action === 'heal'
                  ? '✨'
                  : ''
        )
        setReaction(
          action.action === 'wash'
            ? 'Espuma, bolhinhas e muito carinho! 🫧'
            : action.action === 'feed' || action.action === 'snack'
              ? 'Hmm… estava uma delícia! ♡'
              : action.action === 'heal'
                ? 'Obrigado por cuidar de mim. ♡'
                : 'Você faz meu dia mais feliz! ♡'
        )
        clearTimeout(reactionTimer.current)
        reactionTimer.current = window.setTimeout(() => setReaction(''), 3500)
      }
    }
    return response
  }

  async function finish(events: GameEvent[]) {
    if (!session) return false
    const response = game.uncertain
      ? await game.retry()
      : await game.send({ action: 'game_finish', session_id: session.id, events })
    const success = !!response && 'state' in response && response.state.session === null
    if (success) {
      completedSession.current = true
      sound.play(3)
    }
    return success
  }

  async function startGame(kind: MiniGameKind, replay = false) {
    if (game.busy || game.loading || (replay && !completedSession.current)) return
    if (!game.uncertain && gameBlockedReason) return
    const result =
      replay && game.uncertain
        ? await game.retry()
        : await game.send({ action: 'game_start', game: kind })
    if (result?.state.session) {
      completedSession.current = false
      setAutoStart(replay)
      setSession(result.state.session)
    }
  }

  function requestClose() {
    if (session && !completedSession.current) setConfirmClose(true)
    else onClose()
  }

  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-label={`Cantinho de ${config.name}`}
      onCancel={(event) => {
        if (event.target !== event.currentTarget) return
        event.preventDefault()
        requestClose()
      }}
    >
      <div
        className={styles.app}
        onPointerDownCapture={sound.unlock}
        onKeyDownCapture={sound.unlock}
      >
        {!showScene && !session && (
          <header className={styles.header}>
            <div className={styles.brand}>
              <span className={styles.brandIcon}>
                <Icon icon="mdi:paw" />
              </span>
              <div>
                <span className={styles.eyebrow}>SEU MASCOTE, SUA COMPANHIA</span>
                <h1 id="mascot-world-title">
                  <span className={styles.mobileBrandName}>{config.name}</span>
                  <span className={styles.desktopBrandName}>
                    Meu cantinho<span>.</span>
                  </span>
                </h1>
              </div>
            </div>
            <div className={styles.headerActions}>
              {state && (
                <button
                  ref={wallet}
                  className={styles.wallet}
                  onClick={() => {
                    setSession(null)
                    openShop('coins')
                  }}
                  disabled={!!session || !appearance?.name}
                  aria-label={`Comprar moedas: ${state.coins} moedas no saldo`}
                  title="Comprar moedas com Pix"
                >
                  <MascotCoin className={styles.walletCoin} />
                  <span className={styles.walletBalance}>
                    <strong>{state.coins}</strong>
                    <small>moedas</small>
                  </span>
                </button>
              )}
              <button
                className={styles.iconButton}
                onClick={sound.toggle}
                aria-label={sound.enabled ? 'Desligar som' : 'Ligar som'}
                aria-pressed={sound.enabled}
              >
                <Icon icon={sound.enabled ? 'mdi:volume-high' : 'mdi:volume-off'} />
              </button>
              <button
                className={styles.iconButton}
                onClick={requestClose}
                aria-label="Fechar jogo do mascote"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </header>
        )}
        {game.uncertain && !session && (
          <div className={styles.pendingAction}>
            <Icon icon="mdi:cloud-alert-outline" aria-hidden="true" />
            <span>Uma ação precisa ser confirmada.</span>
            <button onClick={() => void game.retry()} disabled={game.busy}>
              Confirmar ação
            </button>
          </div>
        )}
        {session ? (
          <MascotMiniGame
            key={session.id}
            session={session}
            mascot={config}
            avatar={avatar}
            onFinish={finish}
            autoStart={autoStart}
            onRestart={() => void startGame(session.kind, true)}
            restartBlockedReason={game.uncertain ? '' : gameBlockedReason}
            restartPurchase={gamePurchaseOptions}
            restartLabel={game.uncertain ? 'Confirmar nova partida' : 'Tentar novamente'}
            onBack={() => {
              setSession(null)
              setRoom('arcade')
            }}
            playSound={sound.play}
            resultMessage={game.message}
            error={game.error}
            busy={game.busy}
            wallet={
              <button
                ref={wallet}
                className={styles.wallet}
                disabled
                aria-label={`Seu saldo: ${state?.coins ?? 0} moedas`}
              >
                <MascotCoin className={styles.walletCoin} />
                <span className={styles.walletBalance}>
                  <strong>{state?.coins ?? 0}</strong>
                  <small>moedas</small>
                </span>
              </button>
            }
          />
        ) : (
          <>
            <div className={styles.workspace}>
              {appearance?.name && state && (
                <nav className={styles.navigation} aria-label="Cômodos do mascote">
                  <span className={styles.navTitle}>EXPLORE SEU CANTINHO</span>
                  {menuRooms.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => goToRoom(item.id)}
                      aria-current={!shop && room === item.id ? 'page' : undefined}
                      aria-label={item.name}
                      title={item.name}
                    >
                      {item.id === 'wardrobe' ? (
                        <span className={styles.mascotNavArtwork} aria-hidden>
                          <DonorMascot config={config} showBackdrop={false} />
                        </span>
                      ) : (
                        <Icon icon={item.icon} />
                      )}
                      <span>{item.name}</span>
                    </button>
                  ))}
                  <button
                    className={styles.shopNav}
                    onClick={() => openShop()}
                    aria-current={shop ? 'page' : undefined}
                    aria-label="Lojinha"
                  >
                    <Icon icon="mdi:store-outline" />
                    <span>Lojinha</span>
                  </button>
                  <div className={styles.navFoot}>
                    <Icon icon="mdi:heart-outline" />
                    <p>
                      Uma amizade que cresce
                      <br />
                      com cada cuidado.
                    </p>
                  </div>
                </nav>
              )}
              <div className={styles.content}>
                {showScene && state && (
                  <div className={styles.sceneDock}>
                    <MascotScene
                      mascot={config}
                      state={state}
                      room={room}
                      now={game.now}
                      reaction={reaction}
                      petReaction={petReaction}
                      careEffect={careEffect}
                      disabled={false}
                      onPet={() => void act({ action: 'pet' })}
                      onRoom={goToRoom}
                    />
                    <div className={styles.sceneActions}>
                      <button
                        className={styles.iconButton}
                        onClick={requestClose}
                        aria-label="Fechar jogo do mascote"
                      >
                        <Icon icon="mdi:close" />
                      </button>
                      <button
                        className={styles.iconButton}
                        onClick={sound.toggle}
                        aria-label={sound.enabled ? 'Desligar som' : 'Ligar som'}
                        aria-pressed={sound.enabled}
                      >
                        <Icon
                          icon={sound.enabled ? 'mdi:volume-high' : 'mdi:volume-off'}
                        />
                      </button>
                      <button
                        className={`${styles.iconButton} ${styles.sceneRefresh}`}
                        onClick={() => void game.refresh()}
                        disabled={disabled}
                        aria-label="Atualizar progresso"
                      >
                        <Icon icon="mdi:refresh" />
                      </button>
                    </div>
                    <button
                      ref={wallet}
                      className={`${styles.wallet} ${styles.sceneWallet}`}
                      onClick={() => {
                        setSession(null)
                        openShop('coins')
                      }}
                      disabled={!!session || !appearance?.name}
                      aria-label={`Comprar moedas: ${state.coins} moedas no saldo`}
                      title="Comprar moedas com Pix"
                    >
                      <MascotCoin className={styles.walletCoin} />
                      <span className={styles.walletBalance}>
                        <strong>{state.coins}</strong>
                        <small>moedas</small>
                      </span>
                    </button>
                  </div>
                )}
                <div
                  className={styles.contentScroll}
                  ref={panel}
                  role="region"
                  aria-label="Conteúdo do mascote"
                >
                  {!data && game.loading ? (
                    <div className={styles.worldLoading} role="status" aria-live="polite">
                      <div className={styles.loadingOrbit} aria-hidden="true">
                        <Icon icon="mdi:star-four-points" />
                      </div>
                      <span>Preparando o cantinho…</span>
                    </div>
                  ) : !data ? (
                    <div className={styles.loading}>
                      <Icon icon="mdi:cloud-alert-outline" />
                      <h2>Não conseguimos abrir seu cantinho.</h2>
                      {game.error && <p role="alert">{game.error}</p>}
                      <button
                        className={styles.primaryButton}
                        onClick={() => void game.refresh()}
                      >
                        Tentar novamente
                      </button>
                    </div>
                  ) : !appearance?.name ? (
                    <div className={styles.adoption}>
                      <span className={styles.eyebrow}>TODA AMIZADE TEM UM COMEÇO</span>
                      <div className={styles.adoptionArt}>
                        <DonorMascot
                          config={
                            requiredSpecies === 'jaguar'
                              ? config
                              : isSheepAvailable(data?.catalog)
                                ? {
                                    ...DEFAULT_MASCOT,
                                    ...SHEEP_DEFAULTS,
                                    name: 'Algodão Doce',
                                  }
                                : DEFAULT_MASCOT
                          }
                        />
                      </div>
                      <h2>
                        Alguém está esperando
                        <br />
                        por você.
                      </h2>
                      <p>
                        {requiredSpecies === 'jaguar'
                          ? 'A onça-pintada é sua companheira nesta campanha. Dê um nome e comece a cuidar dela.'
                          : restrictedSheep
                            ? 'Sua ovelhinha é exclusiva da Agroplay. Volte a uma campanha da Agroplay para cuidar dela. Escolher outro mascote aqui substitui a ovelhinha e preserva suas moedas e seu progresso.'
                            : 'Escolha seu companheiro, dê um nome e comece uma história de cuidados, descobertas e brincadeiras.'}
                      </p>
                      <button
                        className={styles.primaryButton}
                        onClick={() => setCustomize(true)}
                      >
                        <Icon icon="mdi:paw" />
                        {restrictedSheep
                          ? 'Escolher outro mascote'
                          : 'Adotar meu mascote'}
                      </button>
                      <small>
                        {restrictedSheep
                          ? 'A ovelhinha continua salva até você confirmar uma troca.'
                          : 'Comidinhas, brincadeiras e muito carinho esperam por você.'}
                      </small>
                    </div>
                  ) : (
                    <>
                      {(shop ||
                        room === 'journal' ||
                        room === 'community' ||
                        room === 'arcade') && (
                        <div className={styles.contentHeading}>
                          <h2>{shop ? 'Escolha algo especial.' : activeRoom.title}</h2>
                          <button
                            className={styles.refresh}
                            onClick={() => void game.refresh()}
                            disabled={disabled}
                            aria-label="Atualizar progresso"
                          >
                            <Icon icon="mdi:refresh" />
                            <span>{game.loading ? 'Atualizando…' : 'Atualizar'}</span>
                          </button>
                        </div>
                      )}
                      {shop ? (
                        <MascotShop
                          key={shopCategory}
                          data={data}
                          send={act}
                          disabled={disabled}
                          initialCategory={shopCategory}
                          onCoinsPurchased={game.refresh}
                          onClose={() => setShop(false)}
                        />
                      ) : room === 'veterinary' ? (
                        <MascotVeterinary
                          campaignId={campaignId}
                          name={config.name}
                          mascot={config}
                          health={currentNeeds?.health ?? 100}
                          disabled={disabled}
                          onPaid={async () => {
                            await game.refresh()
                            onVeterinaryPaid?.()
                          }}
                        />
                      ) : room === 'journal' ? (
                        <MascotJournal
                          data={data}
                          send={act}
                          disabled={disabled}
                          now={game.now}
                        />
                      ) : room === 'community' ? (
                        <MascotCommunity
                          items={community.items}
                          phase={community.phase}
                          onRetry={() =>
                            setAppearanceRevision((revision) => revision + 1)
                          }
                        />
                      ) : room === 'arcade' ? (
                        <section className={styles.arcade} aria-label="Minijogos">
                          <div className={styles.arcadeBanner}>
                            <span>✦</span>
                            <div>
                              <strong>Diversão que vira cuidado.</strong>
                              <p>Jogue, ganhe moedas e transforme seu cantinho.</p>
                            </div>
                            <Icon icon="mdi:controller-classic-outline" />
                          </div>
                          {state?.sleeping && (
                            <div className={styles.sleepNotice}>
                              <p>Seu mascote está dormindo. Acorde para brincar.</p>
                              <button
                                className={styles.primaryButton}
                                onClick={() => void act({ action: 'wake' })}
                                disabled={disabled}
                              >
                                Acordar mascote
                              </button>
                            </div>
                          )}
                          {gamePurchaseOptions}
                          <div className={styles.gameGrid}>
                            {miniGamesForHabitat(jungle, config.species === 'sheep').map(
                              (item, index) => (
                                <button
                                  key={item.id}
                                  className={styles.gameCard}
                                  data-game={item.id}
                                  data-waiting={gameWait > 0}
                                  disabled={disabled || !!gameBlockedReason}
                                  onClick={() => void startGame(item.id)}
                                >
                                  <div className={styles.gameArt}>
                                    <MiniGameArtwork
                                      kind={item.id}
                                      sheep={config.species === 'sheep'}
                                    />
                                    <i>0{index + 1}</i>
                                  </div>
                                  <div className={styles.gameCardCopy}>
                                    <small>
                                      {item.tag} ·{' '}
                                      {item.id === 'frisbee'
                                        ? '10 MIN · ATÉ 48 MOEDAS'
                                        : (game.data?.catalog.challenge?.name ??
                                          'Explorador')}
                                    </small>
                                    <h3>{item.name}</h3>
                                    <p>{item.description}</p>
                                    <footer>
                                      <span>
                                        Recorde: {state?.best_scores[item.id] ?? 0}
                                      </span>
                                      {gameWait > 0 ? (
                                        <ActionCountdown
                                          seconds={gameWait}
                                          label="Jogar em"
                                        />
                                      ) : (
                                        <b>
                                          {gameBlockedReason || 'Jogar'}{' '}
                                          <Icon
                                            icon={
                                              gameWait > 0
                                                ? 'mdi:timer-outline'
                                                : gameBlockedReason
                                                  ? 'mdi:information-outline'
                                                  : 'mdi:arrow-right'
                                            }
                                          />
                                        </b>
                                      )}
                                    </footer>
                                  </div>
                                </button>
                              )
                            )}
                          </div>
                          <p className={styles.caption}>
                            Cada partida custa{' '}
                            {game.data?.catalog.rules?.game_energy ?? 8} de energia ao
                            iniciar. Até {game.data?.catalog.challenge?.max_coins ?? 14}{' '}
                            moedas por partida perfeita. Hoje:{' '}
                            {state?.daily.game_coins ?? 0}/
                            {game.data?.catalog.rules?.game_coin_limit ?? 400} moedas. A
                            dificuldade sobe nos níveis 4, 8 e 12. Na Busca do frisbee, o
                            ritmo acelera com o tempo: até 30 moedas para pegar nos
                            saltos, mais 1 a cada 500 pontos, até 18 pela pontuação, antes
                            dos limites da carteira.
                          </p>
                        </section>
                      ) : (
                        state && (
                          <div className={styles.roomPanelWrap}>
                            <MascotRoomPanel
                              data={data}
                              room={room}
                              now={game.now}
                              send={act}
                              disabled={disabled}
                              onShop={openShop}
                              onCustomize={() => setCustomize(true)}
                              onRoom={goToRoom}
                              onPurchase={setCarePurchase}
                              mascotArtwork={
                                <DonorMascot config={config} showBackdrop={false} />
                              }
                            />
                            {room === 'bedroom' && (
                              <MascotEnergyPill
                                campaignId={campaignId}
                                energy={currentNeeds?.energy ?? 100}
                                disabled={disabled}
                                onPaid={game.refresh}
                              />
                            )}
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {(game.busy || game.toast) && data && (
              <div
                className={`${styles.feedback} ${game.toast?.kind === 'error' ? styles.feedbackError : ''}`}
              >
                <Icon
                  icon={
                    game.busy
                      ? 'mdi:loading'
                      : game.toast?.kind === 'error'
                        ? 'mdi:alert-circle-outline'
                        : 'mdi:check-circle-outline'
                  }
                />
                <span role={game.toast?.kind === 'error' ? 'alert' : 'status'}>
                  {game.busy ? 'Salvando no seu cantinho…' : game.toast?.text}
                </span>
              </div>
            )}
          </>
        )}
        {coinRewards.reward && (
          <MascotCoinReward
            key={coinRewards.reward.id}
            reward={coinRewards.reward}
            walletRef={wallet}
            onComplete={coinRewards.complete}
            playCoin={sound.playCoin}
          />
        )}
        {carePurchase && (
          <MascotCarePurchaseDialog
            campaignId={campaignId}
            benefit={carePurchase}
            energy={state ? mascotCareValue(carePurchase, state, game.now) : 100}
            disabled={disabled}
            onPaid={game.refresh}
            onClose={() => setCarePurchase(null)}
          />
        )}
        {confirmClose && (
          <GameConfirm
            title="Voltar ao perfil?"
            description="Os cuidados já salvos ficam guardados. A partida em andamento será abandonada."
            leaveLabel="Voltar ao perfil"
            onContinue={() => setConfirmClose(false)}
            onLeave={onClose}
          />
        )}
      </div>
      {customize && (
        <Suspense
          fallback={
            <p className={styles.editorLoading} role="status">
              Abrindo ateliê…
            </p>
          }
        >
          <MascotEditor
            initial={appearance ? parseMascotConfig(config) : null}
            requiredBackdrop={requiredBackdrop}
            requiredSpecies={requiredSpecies}
            availableSpecies={data?.catalog.mascot_species?.filter(
              (species) => species !== 'sheep' || isSheepAvailable(data.catalog)
            )}
            onClose={() => setCustomize(false)}
            onSave={async (draft) => {
              const saved = await saveDonorMascot(draft, campaignId)
              setAppearance(saved)
              onSaved(saved)
              setAppearanceRevision((value) => value + 1)
              await game.refresh()
            }}
          />
        </Suspense>
      )}
    </dialog>,
    document.body
  )
}
